import { useMemo, type ReactElement } from 'react'
import { ArrowLeft, Loader2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router'
import { useListProductSources } from '@/shared/hooks/api/productSource'
import { Button } from '@/components/ui/button'
import { ProductCatalogIngestionStatus } from '@/features/product-catalog/components/ProductCatalogIngestionStatus'
import { ProductSourceInspectSection } from '@/features/product-catalog/components/ProductSourceInspectSection'

export function ProductSourceDetail({
  sourceId,
  active = true,
  onBackToProduct,
}: {
  sourceId: string
  active?: boolean
  onBackToProduct: () => void
}): ReactElement {
  const { t } = useTranslation('productCatalog')
  const params = useParams<{ productId: string }>()
  const productId = params.productId!

  const { data: sources = [], isLoading: isSourcesLoading } =
    useListProductSources(productId)

  const source = useMemo(
    () => sources.find((row) => row.id === sourceId),
    [sources, sourceId]
  )

  const showSourceLoading = isSourcesLoading && source == null
  const showSourceNotFound = !isSourcesLoading && source == null

  const sourceTitle = source?.name?.trim() ?? t('sources.inspect.title')

  return (
    <div className='flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto sm:gap-6'>
      <div className='space-y-4'>
        <Button
          type='button'
          variant='ghost'
          size='sm'
          className='gap-2'
          onClick={onBackToProduct}
        >
          <ArrowLeft className='size-4' />
          {t('sources.inspect.backToProduct')}
        </Button>

        {showSourceLoading && (
          <div className='flex items-center justify-center py-16'>
            <Loader2 className='size-8 animate-spin text-muted-foreground' />
          </div>
        )}

        {showSourceNotFound && (
          <p className='text-muted-foreground'>{t('detail.notFound')}</p>
        )}

        {source != null && (
          <div className='space-y-6'>
            <div className='flex flex-wrap items-center justify-between gap-4'>
              <h1 className='truncate text-2xl font-bold tracking-tight'>
                {sourceTitle}
              </h1>
              <ProductCatalogIngestionStatus
                productId={productId}
                sourceId={source.id}
                status={source.ingestionStatus}
                fileUploadStatus={source.fileUploadStatus}
                filePathInBucket={source.filePathInBucket}
                stage={source.ingestionStage}
                progress={source.ingestionProgress}
                ingestionErrorCode={source.ingestionErrorCode}
                sourceContentType={source.type}
                sourceFileName={source.name}
                partialResult={source.ingestionPartialResult}
                missingPages={source.ingestionMissingPages}
              />
            </div>

            <ProductSourceInspectSection
              productId={productId}
              source={source}
              enabled={active}
            />
          </div>
        )}
      </div>
    </div>
  )
}
